/**
 * Operator-facing labels — collection credit hold recommendation UI (Slice D).
 */

import type { CollectionCreditHoldDeferReasonCode } from '@shared/collection/collection-credit-hold-recommendation-disposition-types';
import { COLLECTION_CREDIT_HOLD_MANDATORY_CAVEAT } from '@shared/collection/collection-credit-hold-recommendation-types';

export const COLLECTION_CREDIT_HOLD_OPERATOR_LABELS = {
  pageTitle: '신용 보류 검토 추천',
  pageSubtitle:
    '고객 단위 신용 보류 검토 추천을 확인하고 조치합니다. 추천은 자동 실행되지 않습니다.',
  panelTitle: '신용 보류 검토 추천 큐',
  boundaryCopy:
    '아래 추천은 신용 보류를 자동 실행하지 않습니다. 권한 있는 담당자의 별도 검토가 필요합니다.',
  mandatoryCaveatTitle: '자동 실행 없음',
  mandatoryCaveatBody:
    '이 추천은 신용 보류를 자동 실행하지 않습니다. 권한 있는 담당자의 별도 검토가 필요합니다.',
  loadFailed: '신용 보류 검토 추천을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  permissionUnavailable: '신용 보류 검토 추천을 조회할 권한이 없습니다.',
  emptyQueue: '현재 검토 대기 중인 신용 보류 추천이 없습니다.',
  loading: '신용 보류 검토 추천을 불러오는 중…',
  customerLabel: '고객',
  generatedAtLabel: '추천 생성 시각',
  ruleProfileVersionLabel: '규칙 프로필 버전',
  reasonCodesLabel: '추천 사유',
  explanationLabel: '설명',
  sourceSummaryLabel: '근거 요약',
  openExposureLabel: '미수금 합계',
  overdueExposureLabel: '연체 미수금',
  maxDpdLabel: '최대 연체일',
  openArCountLabel: '미결 AR 건수',
  brokenCommitmentLabel: '불이행 약속',
  worstStageLabel: '최악 수금 단계',
  dispositionLabel: '조치 상태',
  deferredBadge: '검토 보류',
  deferredUntil: '보류 만료',
  deferReason: '보류 사유',
  escalatedBadge: '신용 검토 전달됨',
  escalatedAt: '전달 시각',
  dismissAction: '추천 기각',
  dismissSubmitting: '기각 중…',
  deferAction: '검토 보류',
  deferSubmitting: '보류 중…',
  cancelDeferAction: '보류 해제',
  cancelDeferSubmitting: '해제 중…',
  escalateAction: '신용 검토로 전달',
  escalateSubmitting: '전달 중…',
  escalateSuccessHint:
    '신용 검토 전달 표시가 기록되었습니다. 신용 보류가 적용되거나 주문이 차단되지 않습니다.',
  deferModalTitle: '신용 보류 검토 추천 보류',
  deferModalBody:
    '해당 추천만 지정 시점까지 목록에서 숨깁니다. 신용 보류가 적용되지 않으며 AR·주문·신용 한도는 변경되지 않습니다.',
  deferReasonLabel: '보류 사유',
  deferReasonPlaceholder: '사유 선택',
  deferExpiryLabel: '보류 만료 시각',
  deferExpiryPlaceholder: '만료 시각 선택',
  deferNotesLabel: '메모 (선택)',
  deferNotesPlaceholder: '추가 메모',
  deferConfirm: '보류 적용',
  deferCancel: '취소',
  deferValidationReason: '보류 사유를 선택해 주세요.',
  deferValidationExpiry: '만료 시각을 선택해 주세요.',
  deferValidationExpiryFuture: '만료 시각은 현재 이후여야 합니다.',
  deferValidationNotesOther: '기타 사유는 메모가 필요합니다.',
  deferValidationNotesLength: '메모는 500자 이내로 입력해 주세요.',
  staleSourceMessage:
    '추천의 근거 정보가 변경되었습니다. 최신 상태를 불러온 뒤 다시 검토해 주세요.',
  inactiveRecommendationMessage: '해당 추천은 더 이상 조치할 수 없습니다. 목록을 새로고침합니다.',
  idempotencyConflictMessage: '동일 요청이 처리 중입니다. 잠시 후 다시 시도해 주세요.',
  mutationFailed: '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  permissionDismissDenied: '추천 기각 권한이 없습니다.',
  permissionDeferDenied: '검토 보류 권한이 없습니다.',
  permissionEscalateDenied: '신용 검토 전달 권한이 없습니다.',
  includeDeferredFilter: '보류 항목 포함',
  includeDeferredHint: '보류 중인 추천도 목록에 표시합니다.',
  refreshAfterMutation: '목록 새로고침',
  fingerprintMissing: '추천 대상 식별 정보가 없어 조치할 수 없습니다.',
  notesMaxLength: 500,
  forbiddenCopyApply: '신용 보류 적용',
  forbiddenCopyBlock: '고객 차단',
  forbiddenCopyOrderBlock: '주문 차단',
  forbiddenCopyApproved: '보류 승인',
} as const;

const DEFER_REASON_LABELS: Record<CollectionCreditHoldDeferReasonCode, string> = {
  awaiting_customer_response: '고객 응답 대기',
  pending_internal_review: '내부 검토 대기',
  scheduled_follow_up: '예정된 후속 조치',
  operator_capacity: '운영자 처리 용량',
  other: '기타',
};

const REASON_CODE_LABELS: Record<string, string> = {
  CH_INELIGIBLE_NO_OPEN_AR: '미결 AR 없음',
  CH_OVERDUE_EXPOSURE_THRESHOLD: '연체 미수금 임계 초과',
  CH_TOTAL_EXPOSURE_THRESHOLD: '총 미수금 임계 초과',
  CH_AGING_MAX_DPD_THRESHOLD: '최대 연체일 임계 초과',
  CH_BREACH_PATTERN_THRESHOLD: '약속 불이행 패턴',
  CH_STAGE_WORST_THRESHOLD: '수금 단계 악화',
};

const CAVEAT_LABELS: Record<string, string> = {
  [COLLECTION_CREDIT_HOLD_MANDATORY_CAVEAT]:
    COLLECTION_CREDIT_HOLD_OPERATOR_LABELS.mandatoryCaveatBody,
};

export function labelCreditHoldDeferReason(code: CollectionCreditHoldDeferReasonCode): string {
  return DEFER_REASON_LABELS[code] ?? code;
}

export function labelCreditHoldReasonCode(code: string): string {
  return REASON_CODE_LABELS[code] ?? code;
}

export function labelCreditHoldCaveat(code: string): string {
  return CAVEAT_LABELS[code] ?? code;
}

export function formatCreditHoldExplanation(reasonCodes: readonly string[]): string {
  if (reasonCodes.length === 0) {
    return '—';
  }
  return reasonCodes.map((code) => labelCreditHoldReasonCode(code)).join(' · ');
}

export function formatCreditHoldTimestamp(iso: string | undefined): string {
  if (!iso?.trim()) {
    return '—';
  }
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) {
    return iso;
  }
  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(parsed));
}

export function formatCreditHoldExposureAmount(value: string | undefined): string {
  if (!value?.trim()) {
    return '—';
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return new Intl.NumberFormat('ko-KR').format(numeric);
  }
  return value;
}
